import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Sparkles, Users, Zap, ArrowDown, Star } from 'lucide-react';

const HeroSection = ({ onScrollToMembership, onScrollToQuiz }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
      data-testid="hero-section"
    >
      {/* Background Image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_ai-brasil-plans/artifacts/4nfxxgsj_1771939942545.jpg"
          alt="AI Brasil Event"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-surface-dark/80 via-surface-dark/70 to-surface-dark" />
      </div>

      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Animated Grid Pattern */}
        <motion.div 
          className="absolute inset-0 opacity-20"
          animate={{
            backgroundPosition: ['0px 0px', '50px 50px'],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 255, 0, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 255, 0, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
        
        {/* Dynamic Gradient Orbs */}
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full bg-neon-green/20 blur-[120px]"
          animate={{
            x: mousePosition.x * 2,
            y: mousePosition.y * 2,
          }}
          transition={{ duration: 0.3 }}
          style={{ top: '10%', left: '20%' }}
        />
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full bg-gold/15 blur-[100px]"
          animate={{
            x: -mousePosition.x * 1.5,
            y: -mousePosition.y * 1.5,
          }}
          transition={{ duration: 0.3 }}
          style={{ bottom: '10%', right: '15%' }}
        />

        {/* Floating Particles */}
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: Math.random() * 4 + 2,
              height: Math.random() * 4 + 2,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 3 === 0 ? '#00FF00' : i % 3 === 1 ? '#FFD700' : '#22c55e',
            }}
            animate={{
              y: [0, -100, 0],
              x: [0, Math.random() * 50 - 25, 0],
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, type: 'spring', bounce: 0.4 }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neon-green/40 bg-neon-green/10 mb-8 backdrop-blur-sm"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          >
            <Sparkles className="w-4 h-4 text-neon-green" />
          </motion.div>
          <span className="text-neon-green text-sm font-semibold tracking-wide">Maior movimento de IA do Brasil</span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-outfit font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter mb-6"
        >
          <motion.span 
            className="text-white inline-block"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Onde a{' '}
          </motion.span>
          <motion.span 
            className="inline-block relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5, type: 'spring', bounce: 0.3 }}
          >
            <span className="gradient-text-green text-glow-green">Inteligência Artificial</span>
          </motion.span>
          <br className="hidden sm:block" />
          <motion.span 
            className="text-white inline-block"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            encontra a{' '}
          </motion.span>
          <motion.span 
            className="inline-block relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.9, type: 'spring', bounce: 0.3 }}
          >
            <span className="gradient-text-gold text-glow-gold">Inteligência Coletiva</span>
          </motion.span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="text-lg sm:text-xl text-white/80 max-w-3xl mx-auto mb-4 leading-relaxed"
        >
          O ecossistema onde <span className="text-neon-green font-semibold">pessoas reais</span> se conectam,
          constroem e geram <span className="text-gold font-semibold">oportunidades verdadeiras</span> juntas.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-white/60 max-w-2xl mx-auto mb-10 italic"
        >
          Aqui, tecnologia é meio. O verdadeiro valor está nas conexões.
        </motion.p>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.3 }}
          className="flex flex-wrap justify-center gap-8 sm:gap-16 mb-12"
        >
          {[
            { value: '10K+', label: 'Membros ativos', icon: Users },
            { value: '50+', label: 'Cidades', icon: Zap },
            { value: '500+', label: 'Eventos/ano', icon: Star },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              className="text-center group"
              whileHover={{ scale: 1.1, y: -5 }}
              transition={{ type: 'spring', bounce: 0.4 }}
            >
              <motion.div
                className="w-12 h-12 mx-auto mb-3 rounded-xl bg-neon-green/10 border border-neon-green/30 flex items-center justify-center group-hover:bg-neon-green/20 transition-colors"
                whileHover={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.5 }}
              >
                <stat.icon className="w-6 h-6 text-neon-green" />
              </motion.div>
              <div className="font-outfit font-black text-3xl sm:text-4xl text-white">
                {stat.value}
              </div>
              <div className="text-white/50 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          {/* Primary CTA - Vibrant */}
          <motion.button
            onClick={onScrollToMembership}
            className="group relative overflow-hidden px-10 py-5 bg-neon-green text-black font-black rounded-full text-lg flex items-center gap-3 shadow-[0_0_40px_rgba(0,255,0,0.3)]"
            whileHover={{ scale: 1.05, boxShadow: '0 0 60px rgba(0, 255, 0, 0.6)' }}
            whileTap={{ scale: 0.95 }}
            data-testid="hero-cta-membership"
          >
            <motion.span
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.6 }}
            />
            <Star className="w-5 h-5 relative z-10" />
            <span className="relative z-10">Quero Ser Membro</span>
            <ChevronRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
          </motion.button>
          
          {/* Secondary CTA - Quiz */}
          <motion.button
            onClick={onScrollToQuiz}
            className="group px-8 py-4 border-2 border-neon-green/50 text-neon-green font-semibold rounded-full hover:bg-neon-green/10 transition-all flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="hero-cta-quiz"
          >
            <span>Descubra seu Perfil</span>
          </motion.button>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2 text-white/40 cursor-pointer hover:text-neon-green transition-colors"
            onClick={onScrollToMembership}
          >
            <span className="text-xs uppercase tracking-widest">Explore</span>
            <ArrowDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-surface-dark via-surface-dark/50 to-transparent" />
    </section>
  );
};

export default HeroSection;
